import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock, GridCards } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import { Placeholder } from "@/components/Placeholder";
import { Quote } from "lucide-react";

const DIFFERENCES = [
  { title: "We are based in Oman.", desc: "Registered here, working here, building for the regulatory environment you actually operate in." },
  { title: "We build with AI.", desc: "It is how a small senior team ships in weeks what used to need a large team and half a year. We are open about where AI does the work and where a person does." },
  { title: "We own our accelerators.", desc: "Delivery, warehousing, showroom management, governance. You are not billed to rebuild something that already exists and already works." },
  { title: "We stay.", desc: "Most of our clients are on support agreements years after go-live. The build is the start of the relationship." },
  { title: "We say no.", desc: "If your problem does not need custom software, we will tell you. A configured off-the-shelf tool at 200 rials a month is sometimes the right answer, and we would rather say so than take a build fee." },
];

const LOCATIONS = [
  { title: "Oman", desc: "Head office. Client delivery, product management and support." },
  { title: "India", desc: "Engineering, QA and DevOps." },
  { title: "United States", desc: "Product design and AI engineering." },
  { title: "Philippines", desc: "Extended-hours application support." },
];

const BELIEFS = [
  { title: "Software should be boring to run.", desc: "The best system is the one nobody discusses, because it works." },
  { title: "Compliance is a design decision, not a document.", desc: "Build it in at the start and it costs almost nothing. Add it after an audit and it costs a fortune." },
  { title: "An estimate is a promise.", desc: "If we quote it, we hold it. If scope changes, you hear about it before we do the work." },
  { title: "Small and senior beats large and junior.", desc: "You will not be handed to a trainee after the sales call." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <Seo title="About Wafa Solutions" description="An Oman-based technology company and technology partner to Altalib Group. We build software for clients and run our own platforms." />
      <PageHero
        eyebrow="About"
        h1="About Wafa Solutions"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Company", to: "/company" }, { label: "About us" }]}
      />

      <InnerBlock tint="white" heading="An Oman-based technology company that runs its own products.">
        <div className="max-w-3xl space-y-5 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <p>Wafa Solutions LLC is a technology company registered in the Sultanate of Oman, and the technology partner to Altalib Group. We build custom software, SaaS products, mobile applications, AI systems and governance platforms for organisations here and across the region.</p>
          <p>Most software companies only build for other people. We do both.</p>
          <p>Our delivery and logistics platform, our warehouse management system, our showroom platform and Meyar — our enterprise and cyber security GRC platform — are ours. We designed them, we built them, and we run them. More are in build.</p>
          <p>That matters for you. When we tell you an architecture will not survive its second year, or that a feature will cost more to run than it earns, it is because we have paid that bill ourselves.</p>
        </div>
      </InnerBlock>

      <InnerBlock tint="cream" heading="What makes us different">
        <GridCards items={DIFFERENCES} columns={3} />
      </InnerBlock>

      <InnerBlock tint="white" heading="Where we work">
        <GridCards items={LOCATIONS} columns={4} />
        <p className="mt-6 text-sm italic text-wafa-ink/70">Four locations, one delivery lead. You always speak to the same person.</p>
      </InnerBlock>

      <InnerBlock tint="cream" heading="What we believe">
        <GridCards items={BELIEFS} columns={2} />
      </InnerBlock>

      <InnerCTA heading="Talk to us." body="A thirty-minute call with an engineer, not a salesperson." />
    </div>
  );
}
